import { createConnectTransport } from '@connectrpc/connect-web'
import { Code, ConnectError, type Interceptor, type Transport } from '@connectrpc/connect'
import { ensureCsrfCookie, readCsrfCookie } from './csrf'

/**
 * Creates the Connect transport the rest of the app consumes via inject().
 *
 * The refreshHook runs exactly once per failing request when the server
 * returns Unauthenticated. It POSTs /auth/refresh (which rotates the opaque
 * refresh cookie server-side and mints a fresh access JWT cookie) and, on
 * success, lets the caller retry transparently. A second failure bubbles up
 * so the router guard can redirect to /auth/login.
 */
export interface CreateTransportOptions {
  baseUrl: string
  refreshHook: () => Promise<boolean>
}

export interface AuthCapabilityProvider {
  get: () => Promise<string>
  clear: () => void
}

export function createAuthCapabilityProvider(): AuthCapabilityProvider {
  let capability = ''
  let pending: Promise<string> | null = null

  const get = async () => {
    if (capability) return capability
    if (pending) return pending
    pending = (async () => {
      const response = await fetch('/auth/payment-token', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error('Auth session is required')
      const body = (await response.json()) as { payment_capability?: unknown }
      if (typeof body.payment_capability !== 'string' || !body.payment_capability) {
        throw new Error('Auth capability is required')
      }
      capability = body.payment_capability
      return capability
    })()
    try {
      return await pending
    } finally {
      pending = null
    }
  }

  return { get, clear: () => { capability = '' } }
}

const authCapability = createAuthCapabilityProvider()

export function getAuthCapability(): Promise<string> {
  return authCapability.get()
}

export function clearAuthCapability(): void {
  authCapability.clear()
}

// createAuthInterceptor wraps a refresh-on-unauth flow around any
// unary/streaming request. Exported so tests can drive it without
// pulling in the full Connect transport machinery.
export function createAuthInterceptor(
  refreshHook: () => Promise<boolean>,
  onRefresh?: () => void,
): Interceptor {
  return (next) => async (req) => {
    try {
      return await next(req)
    } catch (err) {
      if (!isUnauthenticated(err)) throw err
      const refreshed = await refreshHook()
      if (!refreshed) throw err
      onRefresh?.()
      return next(req)
    }
  }
}

export function createBearerInterceptor(provider: AuthCapabilityProvider['get']): Interceptor {
  return (next) => async (req) => {
    try {
      req.header.set('Authorization', `Bearer ${await provider()}`)
    } catch {
      return next(req)
    }
    return next(req)
  }
}

export function createTransport(opts: CreateTransportOptions): Transport {
  const authInterceptor = createAuthInterceptor(opts.refreshHook, clearAuthCapability)

  return createConnectTransport({
    baseUrl: opts.baseUrl,
    fetch: async (input, init) => {
      const headers = new Headers(init?.headers ?? {})
      if (!headers.has('X-CSRF-Token')) {
        const csrf = readCsrfCookie() || (await ensureCsrfCookie())
        headers.set('X-CSRF-Token', csrf)
      }
      return fetch(input, { ...init, headers, credentials: 'include' })
    },
    useBinaryFormat: false,
    interceptors: [authInterceptor, createBearerInterceptor(getAuthCapability)],
  })
}

function isUnauthenticated(err: unknown): boolean {
  return err instanceof ConnectError && err.code === Code.Unauthenticated
}
